import { AfterContentChecked, Component, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { AppComponent } from '@core/app.component';
import { BaseComponent } from '@core/components/base-component';
import { BudgetTypeEnum } from '@enums/BudgetType';
import { EstimateRowTypeEnum } from '@enums/EstimateRowType';
import { PopEntityStateEnum } from '@enums/PopEntityState';
import { StatusEnum } from '@enums/Status';
import { StructureDepthEnum } from '@enums/StructureDepth';
import { AverageRateDto } from '@interfaces/AverageRateDto';
import { Estimate } from '@interfaces/Estimate';
import { EstimateConfiguration } from '@interfaces/EstimateConfiguration';
import { UserDto } from '@interfaces/UserDto';
import {
  SECTION_FILTERS_STORAGE_KEY,
  SECTION_FILTER_ITEMS,
  SectionFilterItem
} from '@models/consts/estimate-section-filter-items.const';
import { TranslateService } from '@ngx-translate/core';
import { inOutAnimation } from '@shared/animations';
import { AppSettingsService } from '@shared/services/app-settings.service';
import { CurrencyService } from '@shared/services/currency.service';
import { EstimateBudgetService } from '@shared/services/estimate-budget.service';
import { EstimateSettingsService } from '@shared/services/estimate-settings.service';
import { EstimateService } from '@shared/services/estimate.service';
import { ExcelService } from '@shared/services/excel.service';
import { LoadingService } from '@shared/services/loading.service';
import { ProjectTotals } from '@shared/services/models/project-totals.model';
import { TotalsDataService } from '@shared/services/total.service';
import { UserService } from '@shared/services/user.service';
import { ValidationsService } from '@shared/services/validations.service';
import { DateTime } from 'luxon';
import { ConfirmationService } from 'primeng/api';
import { EMPTY, Subscription, combineLatest, firstValueFrom, from, timer } from 'rxjs';
import { switchMap, take } from 'rxjs/operators';
import { EstimateRowModel } from '../models/EstimateRow';
import { DeployDialogComponent } from './deploy-dialog/deploy-dialog.component';
import { TreeNodeData } from './treetable/models/tree-node-data.model';
import { TreetableComponent } from './treetable/treetable.component';

@Component({
  selector: 'pop-estimate',
  templateUrl: './estimate.component.html',
  styleUrls: ['./estimate.component.less'],
  encapsulation: ViewEncapsulation.None,
  animations: [inOutAnimation]
})
export class EstimateComponent extends BaseComponent implements OnInit, OnDestroy, AfterContentChecked {
  private averageRates: AverageRateDto[];
  private currentEstimate: Estimate;
  public departmentLabel = '';
  private estimateId: string;
  public estimateSectionFilterItems: SectionFilterItem[] = [];
  public functionalCurrencySymbol: string;
  public hasCRM: boolean;
  public isDetailedBudgetType = false;
  public isGroupApproved: boolean;
  public isPanelOpen = false;
  private projectTotals: ProjectTotals;
  public settings: EstimateConfiguration;
  public showApproveButton: boolean;
  public showSendButton: boolean;
  public showLongLoadingScreen = true;
  public showRejectBtn = false;

  // selected Tab
  public isProjectDisplayed = true;
  public isDepartmentDisplayed: boolean;
  public isCategoryDisplayed: boolean;
  public isClassDisplayed: boolean;

  @ViewChild('treeTable', { static: false })
  public treeTable: TreetableComponent;

  public constructor(
    private estimateService: EstimateService,
    private estimateBudgetService: EstimateBudgetService,
    private titleService: Title,
    private route: ActivatedRoute,
    private router: Router,
    private snackBar: MatSnackBar,
    private currencyService: CurrencyService,
    private confirmationService: ConfirmationService,
    private totalsData: TotalsDataService,
    private translate: TranslateService,
    private validationsService: ValidationsService,
    private excelService: ExcelService,
    private estimateSettingsService: EstimateSettingsService,
    private appSettingsService: AppSettingsService,
    private userService: UserService,
    private loadingService: LoadingService,
    private dialog: MatDialog
  ) {
    super();
  }

  public async ngOnInit() {
    this.subscribe(
      this.route.params.subscribe((params: { id: string }) => {
        this.estimateId = params.id;
        this.refreshCurrentEstimate();
      })
    );

    this.subscribe(
      this.estimateService.dialogHasChanges().subscribe(() => {
        this.refreshCurrentEstimate();
      })
    );

    this.subscribe(
      combineLatest({
        estimate: this.estimateService.observeCurrentEstimate(),
        isGroupApproved: this.estimateService.observeIsCurrentEstimateGroupApproved(),
        estimateConfig: this.estimateSettingsService.getEstimateConfiguration(false),
        appSettings: this.appSettingsService.getAppSettings(),
        user: this.userService.getUser()
      })
        .pipe(
          switchMap(results => {
            this.currentEstimate = results.estimate;
            this.isGroupApproved = results.isGroupApproved;

            this.settings = results.estimateConfig;
            this.estimateSectionFilterItems = this.getSectionFiltersFromStorage(this.settings);

            this.hasCRM = results.appSettings.hasCRM;

            this.verifyUser(results.user, this.currentEstimate.companyCode);

            this.isDetailedBudgetType = this.currentEstimate.budgetType === BudgetTypeEnum.Detailed;
            this.showSendButton = this.currentEstimate.status === StatusEnum.New && !this.isGroupApproved;
            this.showApproveButton = this.currentEstimate.status === StatusEnum.Waiting && !this.hasCRM;

            this.titleService.setTitle(EstimateService.getTitle(this.currentEstimate));
            return from(this.estimateSettingsService.getDepartmentLabel());
          })
        )
        .subscribe(departmentLabel => {
          this.departmentLabel = departmentLabel;
        })
    );

    this.subscribe(
      new Subscription(() => {
        this.titleService.setTitle(AppComponent.defaultTitle); // restore page title in browser when leaving
      })
    );

    await firstValueFrom(this.estimateService.observeCurrentEstimate());
    this.subscribe(
      combineLatest([
        this.currencyService.getFunctionalCurrency(this.currentEstimate.companyCode),
        this.estimateService.getClassDeptRates(this.currentEstimate.companyCode)
      ]).subscribe(([functionalCurrency, rates]) => {
        this.averageRates = rates;

        if (functionalCurrency) {
          this.functionalCurrencySymbol = functionalCurrency.currency.symbol.trim();
        }
      })
    );
  }

  public ngAfterContentChecked() {
    this.totalsData.currentProjectTotals$.subscribe(projectTotals => (this.projectTotals = projectTotals));
  }

  private verifyUser(currentUser: UserDto, companyCode: string): boolean {
    let hasAccess = true;
    const restrictedCompanies = this.settings.restrictedCompanies.split(',');
    const isAppAdmin = currentUser.roles.some(r => r.name === 'app-admin');

    if (isAppAdmin) {
      hasAccess = true;
    } else if (currentUser.isRestrictedByCompany) {
      hasAccess = companyCode === currentUser.company;
    } else if (restrictedCompanies.includes(companyCode)) {
      hasAccess = false;
    }

    if (!hasAccess) {
      this.showLongLoadingScreen = true;

      const snackBarRef = this.snackBar.open(this.translate.instant('ERROR.COMPANY_ACCESS') as string, 'OK', {
        duration: 500000,
        verticalPosition: 'top'
      });
      snackBarRef.afterDismissed().subscribe(() => {
        void this.router.navigate(['/app/estimate-list']);
      });
    }

    return hasAccess;
  }

  private confirmNoLaborBillingMethod(onAccept: () => Promise<void>) {
    this.confirmationService.confirm({
      message: this.translate.instant('ERROR.NO_METHOD_MARKUP') as string,
      header: this.translate.instant('BUTTON.SUBMIT') as string,
      icon: 'fas fa-exclamation-circle',

      key: 'global-confirmation-dialog',
      acceptLabel: this.translate.instant('BUTTON.CONFIRM') as string,
      rejectLabel: this.showRejectBtn ? (this.translate.instant('BUTTON.CANCEL') as string) : undefined,
      accept: async () => {
        await onAccept();
        this.showRejectBtn = false;
        return EMPTY;
      },
      reject: () => {
        this.showRejectBtn = false;
        return EMPTY;
      }
    });
  }

  private showErrors(errors: string[]) {
    let errorMessage = '';

    for (const error of [...new Set(errors)]) {
      let errorTranslationKey = '';
      switch (error) {
        case 'needsLaborBillingMethod':
          errorTranslationKey = 'ERROR.NO_LUMP_TIME';
          break;
        case 'overBudget':
          errorTranslationKey = 'ERROR.OVERBUDGET.OVERBUDGET_PROJECT';
          break;
        case 'totalBillableFinal':
          errorTranslationKey = 'ERROR.REALLOCATION';
          break;
        case 'alreadyDeployedSubLevels':
          errorTranslationKey = 'ERROR.ALREADY_EXISTING_SUBLEVEL';
          break;
        default:
          errorTranslationKey = 'ERROR.GENERIC';
          break;
      }
      errorMessage += `\u2022 ${this.translate.instant(errorTranslationKey) as string} \n\n`;
    }

    this.confirmationService.confirm({
      message: errorMessage,
      header: this.translate.instant('ERROR.ERROR') as string,
      icon: 'fas fa-exclamation-circle',

      key: 'global-confirmation-dialog',
      acceptLabel: this.translate.instant('BUTTON.OK') as string,
      accept: () => {
        return EMPTY;
      },
      reject: () => {
        return EMPTY;
      }
    });
  }

  public async validateSubmit() {
    const parentNode = this.treeTable.estimateTree[0];

    const errors = await this.loadingService.run(async () => {
      const overBudgetResult = await this.estimateBudgetService.verifyIsOverBudget(
        this.currentEstimate,
        parentNode,
        this.settings.groupBudgetsByProjDept
      );

      return await this.validationsService.validateEstimate(
        this.currentEstimate,
        overBudgetResult.length > 0,
        this.projectTotals.totalBillableFinal
      );
    });

    if (errors.length > 0) {
      this.showErrors(errors);
      return;
    }

    if (!this.currentEstimate.laborBillingMethodCode || this.currentEstimate.laborBillingMethodCode === '') {
      this.showRejectBtn = true;
      this.confirmNoLaborBillingMethod(async () => {
        await this.submitEstimate();
      });
      return;
    }

    await this.submitEstimate();
  }

  private updateRates() {
    // Bug 419: [Estimate] Capture Rates on Send
    // The estimate must conserve the rateCost from the average rates table (class department rates) when sent
    this.currentEstimate.departmentEstimates.forEach(departmentEstimate => {
      const deptartmentCode = departmentEstimate.department.code;

      departmentEstimate.estimateLines.forEach(estimateLine => {
        estimateLine.classificationLines.forEach(classificationLine => {
          const classificationEstimate = classificationLine.classificationEstimate;

          const classCode = classificationEstimate.classification.code;
          const currentRate = this.averageRates.find(
            r => r.classificationCode.toUpperCase() === classCode.toUpperCase() && r.departmentCode === deptartmentCode
          );

          classificationLine.rateCost = currentRate?.rate ?? 0;
          classificationLine.rateCostDepartment = currentRate?.sourceRate;
        });
      });
    });
  }

  private async saveRates() {
    this.updateRates();
    await this.estimateService.saveChangesAsync();
  }

  private async submitEstimate() {
    await this.loadingService.run(async () => {
      await this.saveRates();

      await this.estimateService.submitEstimate(
        this.currentEstimate.id,
        this.projectTotals.totalHours,
        this.projectTotals.totalHoursCost,
        this.projectTotals.totalHoursBillable,
        this.projectTotals.totalExpensesCost,
        this.projectTotals.totalExpensesBillable,
        this.projectTotals.totalBillableFinal,
        this.hasCRM
      );

      this.currentEstimate.status = StatusEnum.Waiting;

      await this.refreshCurrentEstimate();
    });
  }

  private async awaitCompleteEstimate() {
    const state = await this.estimateService.getEstimatePOPStatus(this.estimateId);

    if (state === PopEntityStateEnum.Modified) {
      await firstValueFrom(timer(2000));
      await this.awaitCompleteEstimate();
    }
    return;
  }

  private async refreshCurrentEstimate() {
    this.currentEstimate = await firstValueFrom(this.estimateService.getEstimateById(this.estimateId)); // triggers next in refesh estimate

    if (this.currentEstimate.popEntityState === PopEntityStateEnum.Modified) {
      await this.awaitCompleteEstimate();
      this.currentEstimate = await firstValueFrom(this.estimateService.getEstimateById(this.estimateId)); // triggers next in refesh estimate
    }
    await this.estimateService.isCurrentEstimateGroupApproved();
    await this.estimateService.getAlreadyDeployedSubLevels();

    this.showLongLoadingScreen = false;
  }

  /**
   * Converts the existing Estimate Tree into a flat list of Estimate Rows,
   * Thus creating an object that can be sent to the API to generate an Excel file
   * */
  private flattenEstimateTree() {
    const rows: EstimateRowModel[] = [];

    // create project row
    const projectRow: EstimateRowModel = new EstimateRowModel(EstimateRowTypeEnum.Project);
    const projectNode = this.treeTable.estimateTree[0];
    const projectNodeData: TreeNodeData = projectNode.data;
    const projectLabel = projectNodeData.label.split('').join('');

    projectRow.setRow(projectNode, this.currentEstimate, projectLabel);
    rows.push(projectRow);

    // create level1 rows
    for (const level1Node of projectNode.children) {
      const level1Row: EstimateRowModel = new EstimateRowModel(EstimateRowTypeEnum.Level1);
      const level1NodeData: TreeNodeData = level1Node.data;
      const level1Label = level1NodeData.label.split('').join('');

      level1Row.setRow(level1Node, this.currentEstimate, projectLabel, level1Label);
      rows.push(level1Row);

      if (this.currentEstimate.structureDepth === StructureDepthEnum.Level2) {
        // create level2 rows
        for (const level2Node of level1Node.children) {
          const level2Row: EstimateRowModel = new EstimateRowModel(EstimateRowTypeEnum.Level2);
          const level2NodeData: TreeNodeData = level2Node.data;
          let level2Label = '';
          if (level2NodeData.type === this.treeTable.level2Type) {
            level2Label = level2NodeData.label.split('').join('');
            level2Row.setRow(level2Node, this.currentEstimate, projectLabel, level1Label, level2Label);
            rows.push(level2Row);
          }

          for (const deptNode of level2Node.children) {
            const deptRow: EstimateRowModel = new EstimateRowModel(EstimateRowTypeEnum.Department);
            const deptNodeData: TreeNodeData = deptNode.data;
            let deptLabel = '';
            if (deptNodeData.type === this.treeTable.departmentType) {
              deptLabel = deptNodeData.label.split('').join('');
              deptRow.setRow(deptNode, this.currentEstimate, projectLabel, level1Label, level2Label, deptLabel);
              rows.push(deptRow);
            }

            if (
              this.currentEstimate.budgetTypeTime === BudgetTypeEnum.Detailed ||
              this.currentEstimate.budgetTypeExpense === BudgetTypeEnum.Detailed ||
              this.currentEstimate.budgetTypeInternalFee === BudgetTypeEnum.Detailed
            ) {
              // create category rows
              for (const catNode of deptNode.children) {
                const catRow: EstimateRowModel = new EstimateRowModel(EstimateRowTypeEnum.Category);
                const catNodeData: TreeNodeData = catNode.data;

                if (catNodeData.type === this.treeTable.categoryType) {
                  const catLabel = catNodeData.label.split('').join('');
                  catRow.setRow(
                    catNode,
                    this.currentEstimate,
                    projectLabel,
                    level1Label,
                    level2Label,
                    deptLabel,
                    catLabel
                  );
                  rows.push(catRow);
                }
              }
            }
          }
        }
      } else {
        // create department rows
        for (const deptNode of level1Node.children) {
          const deptRow: EstimateRowModel = new EstimateRowModel(EstimateRowTypeEnum.Department);
          const deptNodeData: TreeNodeData = deptNode.data;
          let deptLabel = '';
          if (deptNodeData.type === this.treeTable.departmentType) {
            deptLabel = deptNodeData.label.split('').join('');
            deptRow.setRow(deptNode, this.currentEstimate, projectLabel, level1Label, '', deptLabel);
            rows.push(deptRow);
          }

          if (
            this.currentEstimate.budgetTypeTime === BudgetTypeEnum.Detailed ||
            this.currentEstimate.budgetTypeExpense === BudgetTypeEnum.Detailed
          ) {
            // create category rows
            for (const catNode of deptNode.children) {
              const catRow: EstimateRowModel = new EstimateRowModel(EstimateRowTypeEnum.Category);
              const catNodeData: TreeNodeData = catNode.data;

              if (catNodeData.type === this.treeTable.categoryType) {
                const catLabel = catNodeData.label.split('').join('');
                catRow.setRow(catNodeData, this.currentEstimate, projectLabel, level1Label, '', deptLabel, catLabel);
                rows.push(catRow);
              }
            }
          }
        }
      }
    }

    return rows;
  }

  public getSectionFiltersFromStorage(estimateConfig: EstimateConfiguration) {
    // Get section items NOT pre-hidden on the estimate configuration level
    const estimateConfigFilters = SECTION_FILTER_ITEMS.filter(i => estimateConfig[i.key] === false);

    try {
      const storedSectionFiltersStr = localStorage.getItem(SECTION_FILTERS_STORAGE_KEY);
      const storedSectionFilters = storedSectionFiltersStr
        ? (JSON.parse(storedSectionFiltersStr) as Partial<Record<keyof EstimateConfiguration, boolean>>)
        : null;

      if (!storedSectionFilters) return estimateConfigFilters;

      return estimateConfigFilters.map(i => ({ ...i, selected: !!storedSectionFilters[i.key] }));
    } catch (e) {
      return estimateConfigFilters;
    }
  }

  public showProjectTable() {
    this.isProjectDisplayed = true;
    this.isClassDisplayed = false;
    this.isDepartmentDisplayed = false;
    this.isCategoryDisplayed = false;
  }

  public showClassTable() {
    this.isProjectDisplayed = false;
    this.isClassDisplayed = true;
    this.isDepartmentDisplayed = false;
    this.isCategoryDisplayed = false;
  }

  public showDepartmentTable() {
    this.isProjectDisplayed = false;
    this.isClassDisplayed = false;
    this.isDepartmentDisplayed = true;
    this.isCategoryDisplayed = false;
  }

  public showCategoryTable() {
    this.isProjectDisplayed = false;
    this.isClassDisplayed = false;
    this.isDepartmentDisplayed = false;
    this.isCategoryDisplayed = true;
  }

  /**
   * Create rows
   * Generate Excel file
   * Download Excel file
   * */
  public exportEstimate() {
    const rows = this.flattenEstimateTree();

    this.excelService.generateExcel(rows).subscribe(response => {
      this.excelService.downloadFile(response, this.createFileName());
    });
  }

  /**
   * Generates an explicit file name for the Excel File
   * ex. "PROJ1234_REV0_2722020102415"
   * */
  private createFileName(): string {
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const dateLabel = DateTime.local({ zone: userTimezone }).toFormat('ddMMyyyyHHmmss');
    const { projectCode, projectName, revision } = this.currentEstimate;

    return `${projectCode}_${projectName}_REV${revision}_${dateLabel}`;
  }

  public async openDeployDialog() {
    const dialogRef = this.dialog.open(DeployDialogComponent, {
      width: '18rem',
      height: 'fit-content',

      panelClass: 'custom-dialog-container',
      data: {
        estimateId: this.currentEstimate.id
      }
    });

    const result: boolean = await firstValueFrom(dialogRef.afterClosed().pipe(take(1)));

    if (result) {
      this.currentEstimate.status = StatusEnum.Approved;
    }
  }
}
